import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { Info } from 'lucide-react';

import { Button } from 'components/ui/button';
import { VisibilityField } from 'components/blocks/visibility-field';
import { WarningAlert } from 'components/blocks/warning-alert';
import { Checkbox } from 'components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from 'components/ui/dialog';
import { Input } from 'components/ui/input';
import { Textarea } from 'components/ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'components/ui/tooltip';
import { useGetToolById, useUpdateMCPServer } from 'features/chat/agent-chat-module/hooks/use-gpt-chat';
import { useToast } from 'hooks/use-toast';
import { useIsOwner } from 'hooks/use-is-owner';
import { useTranslation } from 'react-i18next';
import { useConfigStore } from 'state/store/config/useConfigStore';
import { DEFAULT_RESOURCE_VISIBILITY, type ResourceVisibility } from 'types/visibility';
import { getToolEditSubject, getToolVisibility } from './tool-detail-utils';
import { getToolFormInputClassName, getToolFormTextareaClassName } from '../tool-form-styles';
import { APPROVAL_TO_VALUE, HumanApprovalField, toApprovalOption, type HumanApprovalOption } from './human-approval';

type EditMcpServerModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tool?: {
    id: string;
    name: string;
    description: string;
    status?: string;
    visibility?: string | null;
    created_by?: string;
    CreatedBy?: string;
  };
  isBlocksTool: boolean;
};

type EditMcpServerFormData = {
  toolName: string;
  description: string;
  visibility: ResourceVisibility;
  isOpenTool: boolean;
  isBlocksAuthenticated: boolean;
  status: boolean;
  humanApproval: HumanApprovalOption;
};

export function EditMcpServerModal({ open, onOpenChange, tool, isBlocksTool }: EditMcpServerModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const configurationSettings = useConfigStore((state) => state.configurationSettings);
  const projectKey = configurationSettings?.blocksKey || '';
  const isAdmin = projectKey === process.env.REACT_APP_PUBLIC_X_BLOCKS_KEY;
  const { mutateAsync: updateMCPServer, isPending } = useUpdateMCPServer();
  const { data: toolDetails } = useGetToolById(
    { toolId: tool?.id || '', projectKey },
    { enabled: open && !!tool?.id && !!projectKey }
  );
  const canEdit = useIsOwner(getToolEditSubject(toolDetails, tool));

  const { register, handleSubmit, formState: { errors }, reset, control, watch } = useForm<EditMcpServerFormData>({
    mode: 'onChange',
    defaultValues: {
      toolName: '',
      description: '',
      visibility: DEFAULT_RESOURCE_VISIBILITY,
      isOpenTool: false,
      isBlocksAuthenticated: false,
      status: false,
      humanApproval: 'auto',
    },
  });

  useEffect(() => {
    if (!open) return;

    const detail = toolDetails?.tool ?? toolDetails?.data;
    if (detail) {
      reset({
        toolName: detail.name || tool?.name || '',
        description: detail.description || tool?.description || '',
        visibility: getToolVisibility(detail),
        isOpenTool: detail.IsOpenTool ?? detail.is_open_tool ?? isBlocksTool,
        isBlocksAuthenticated: detail.IsBlocksAuthenticated ?? false,
        status: detail.status === 'active',
        humanApproval: toApprovalOption(detail.RequiresApproval ?? toolDetails?.RequiresApproval),
      });
      return;
    }

    if (tool) {
      reset({
        toolName: tool.name,
        description: tool.description,
        visibility: getToolVisibility(tool),
        isOpenTool: isBlocksTool,
        isBlocksAuthenticated: false,
        status: tool.status === 'active',
        humanApproval: toApprovalOption(toolDetails?.RequiresApproval),
      });
    }
  }, [open, toolDetails, tool, reset, isBlocksTool]);

  const isBlocksToolChecked = watch('isOpenTool');

  const onSubmit = async (data: EditMcpServerFormData) => {
    if (!canEdit || !tool?.id || !projectKey) return;

    try {
      const response = await updateMCPServer({
        id: tool.id,
        name: data.toolName.trim(),
        description: data.description.trim(),
        project_key: projectKey,
        visibility: data.visibility,
        is_open_tool: isAdmin ? data.isOpenTool : undefined,
        is_blocks_authenticated: isAdmin ? data.isBlocksAuthenticated : undefined,
        status: isAdmin && data.isOpenTool ? (data.status ? 'active' : 'inactive') : undefined,
        requires_approval: APPROVAL_TO_VALUE[data.humanApproval],
      });

      if (!response.is_success) {
        throw new Error(response.message || response.detail || t('FAILED_TO_UPDATE_MCP_SERVER') || 'Failed to update MCP server');
      }

      toast({
        variant: 'success',
        title: t('MCP_SERVER_UPDATED'),
        description: t('MCP_SERVER_UPDATED_SUCCESSFULLY'),
      });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['tools'] }),
        queryClient.invalidateQueries({ queryKey: ['tools', 'detail', tool.id, projectKey] }),
      ]);
      onOpenChange(false);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t('UPDATE_FAILED') || 'Update failed',
        description: error instanceof Error ? error.message : t('FAILED_TO_UPDATE_MCP_SERVER') || 'Failed to update MCP server.',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[640px] rounded-[0.5rem] border-0 p-4 shadow-xl">
        <DialogHeader className="space-y-1 px-0 py-0">
          <DialogTitle className="text-lg font-medium leading-none">
            {canEdit
              ? t('EDIT_MCP_SERVER') || 'Edit MCP server'
              : t('VIEW_MCP_SERVER') || 'View MCP server'}
          </DialogTitle>
          <DialogDescription className="text-sm leading-6">
            {t('EDIT_MCP_SERVER_DESCRIPTION') ||
              'Define how this MCP server will assist your agent in performing tasks.'}
          </DialogDescription>
        </DialogHeader>

        <form className="space-y-4 px-0 py-4" onSubmit={handleSubmit(onSubmit)}>
          <fieldset disabled={!canEdit || isPending} className="m-0 space-y-4 border-0 p-0 disabled:opacity-70">
            {!canEdit ? (
              <WarningAlert>
                {t('YOU_ARE_NOT_THE_OWNER_OF_THIS_TOOL') ||
                  'You are not the owner of this tool.'}
              </WarningAlert>
            ) : null}

            <div className="space-y-2">
              <label htmlFor="toolName" className="text-sm font-medium leading-6">
                {t('TOOL_NAME') || 'Tool Name'} <span className="text-destructive">*</span>
              </label>
              <Input
                id="toolName"
                {...register('toolName', { required: t('TOOL_NAME_REQUIRED') || 'Tool name is required' })}
                className={getToolFormInputClassName(!!errors.toolName)}
              />
              {errors.toolName ? <p className="text-sm text-destructive">{errors.toolName.message}</p> : null}
            </div>

            <Controller
              name="visibility"
              control={control}
              render={({ field }) => (
                <VisibilityField
                  value={field.value}
                  onValueChange={field.onChange}
                  idPrefix="edit-mcp-server-visibility"
                  disabled={!canEdit || isPending}
                />
              )}
            />

            <div className="space-y-2">
              <label htmlFor="description" className="text-sm font-medium leading-6">
                {t('DESCRIPTION') || 'Description'} <span className="text-destructive">*</span>
              </label>
              <Textarea
                id="description"
                rows={4}
                {...register('description', { required: t('DESCRIPTION_REQUIRED') || 'Description is required' })}
                className={getToolFormTextareaClassName(!!errors.description)}
              />
              {errors.description ? <p className="text-sm text-destructive">{errors.description.message}</p> : null}
            </div>

            <HumanApprovalField
              control={control}
              name="humanApproval"
              id="editMcpHumanApproval"
              toolType="mcp"
              disabled={!canEdit || isPending}
            />

            {isBlocksToolChecked ? (
              <div className="pointer-events-none flex items-center gap-2">
                <Controller
                  name="isOpenTool"
                  control={control}
                  render={({ field }) => <Checkbox checked={!!field.value} disabled />}
                />
                <span className="text-sm text-muted-foreground">{t('BLOCKS_TOOL') || 'Blocks Tool'}</span>
              </div>
            ) : null}

            {isAdmin && isBlocksToolChecked ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Controller
                    name="isBlocksAuthenticated"
                    control={control}
                    render={({ field }) => (
                      <Checkbox
                        checked={!!field.value}
                        disabled={!canEdit}
                        onCheckedChange={(checked) => field.onChange(!!checked)}
                      />
                    )}
                  />
                  <span className="text-sm font-medium leading-6">
                    {t('ALLOW_TOOL_AUTHENTICATION') || 'Allow Tool Authentication'}
                  </span>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger type="button" className="cursor-help">
                        <Info className="h-4 w-4 text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="text-sm leading-6">
                          {t('ALLOW_MCP_SERVER_AUTHENTICATION_HELP') ||
                            'If checked, users can copy the MCP server with its authentication.'}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>

                <div className="flex items-center gap-2">
                  <Controller
                    name="status"
                    control={control}
                    render={({ field }) => (
                      <Checkbox
                        checked={!!field.value}
                        disabled={!canEdit}
                        onCheckedChange={(checked) => field.onChange(!!checked)}
                      />
                    )}
                  />
                  <span className="text-sm font-medium leading-6">{t('TOOL_ACTIVE') || 'Tool Active'}</span>
                </div>
              </div>
            ) : null}
          </fieldset>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('CANCEL') || 'Cancel'}
            </Button>
            {canEdit ? (
              <Button type="submit" disabled={isPending}>
                {isPending ? t('SAVING') || 'Saving...' : t('SAVE') || 'Save'}
              </Button>
            ) : null}
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
